import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatestRef } from '@bliks-io/ui';
import { useCreateDepartmentsBulk } from '@/api/mutations/useDepartments';
import { useInitializeAssignUserPlacement } from '@/api/mutations/useInitializeAssignUserPlacement';
import { useInitializeUsers } from '@/api/mutations/useInitializeUsers';
import { useInitializeOrgFunctions } from '@/api/mutations/useInitializeOrgFunctions';
import { useInitializeSystems } from '@/api/mutations/useInitializeSystems';
import { useInitializeProcesses } from '@/api/mutations/useInitializeProcesses';
import type { ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { buildAnchorImportPayload } from '../utils/buildAnchorImportPayload';
import { classifyAnchorDrafts } from '../utils/classifyAnchorDrafts';
import { membershipDuplicateKeys, orgFunctionDuplicateKeys } from '../utils/draftRowValidation';
import { validateDraftRows } from '@/lib/draft-rows/markDuplicateRows';
import {
  effectiveAnchorBaseline,
  serializeSurface,
  type WizardFlushBaselines
} from '../utils/flushBaselines';
import { commitAnchorDelta } from '../utils/commitAnchorDelta';
import {
  commitPlacementRows,
  NO_PROTECTED_IDS,
  partitionProtectedRows,
  requireProtectedCommit
} from '../utils/protectedRows';
import { commitOrgFunctionSurface, stampSurfaceRows } from '../utils/orgFunctionSurfaceWire';
import type { ParsedDepartment, ParsedUser } from '../utils/excelParsers';
import {
  departmentsStepSpec,
  processesStepSpec,
  systemsStepSpec,
  usersStepSpec,
  type AnchorStepSpec,
  type WizardSeeds
} from '../anchorStepSpecs';
import type { OrgFunctionSurfaceDrafts } from '../steps/OrgFunctionSurfaceStep';
import type { WizardDrafts } from '../useWizardSubmit';
import type { WizardValidationContext } from '../useWizardValidationContext';
import type { WizardProgress } from '../wizardSteps';
import { createAutosaveEngine, type EngineSnapshot } from './autosaveEngine';
import { computeWizardDirty } from './dirty';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface WizardAutosave {
  status: AutosaveStatus;
  isDirty: boolean;
  /** Called by the page's draft onChange handlers; reschedules the debounce. */
  notifyChange: () => void;
  /** Cancel the pending debounce and await the in-flight flush (Weiter runs this first). */
  settle: () => Promise<void>;
  /** Suspend while Weiter's own mutation is in flight — see autosaveEngine. */
  setSuspended: (suspended: boolean) => void;
}

interface UseWizardAutosaveArgs {
  drafts: WizardDrafts;
  baselines: WizardFlushBaselines;
  setBaselines: React.Dispatch<React.SetStateAction<WizardFlushBaselines>>;
  seeds: WizardSeeds;
  progress: WizardProgress;
  /** The page's single instance — sharing it with useWizardSubmit is what keeps the two validation passes identical. */
  validation: WizardValidationContext;
  setDepartments: React.Dispatch<React.SetStateAction<ParsedDepartment[]>>;
  setUsers: React.Dispatch<React.SetStateAction<ParsedUser[]>>;
  setFunctionDrafts: React.Dispatch<React.SetStateAction<OrgFunctionSurfaceDrafts>>;
  setTeamDrafts: React.Dispatch<React.SetStateAction<OrgFunctionSurfaceDrafts>>;
  setSystems: React.Dispatch<React.SetStateAction<ParsedSystemRow[]>>;
  setProcesses: React.Dispatch<React.SetStateAction<InitializeProcessDraft[]>>;
}

const AUTOSAVE_DEBOUNCE_MS = 2000;

function deriveStatus(engine: EngineSnapshot, draftsDirty: boolean): AutosaveStatus {
  if (engine.phase === 'flushing') return 'saving';
  if (engine.phase === 'debouncing') return 'pending';
  // A stale failure stops reading as an error once nothing is unsaved anymore
  // (Weiter committed it) — and dirt no debounce will pick up (pending
  // removals, rows still invalid) must read as unsaved, not idle/saved.
  if (engine.lastRound === 'error' && draftsDirty) return 'error';
  if (draftsDirty) return 'pending';
  if (engine.lastRound === 'success') return 'saved';
  return 'idle';
}

// Silent anchor flush: gate on whole-draft validity (marked rows are
// DISCARDED — no error painting from autosave), send the classified delta
// minus removals, stamp the returned ids onto the drafts, and keep removed
// rows in the baseline so the next diff still sees them as removals.
// Protected rows never travel through the add-users pipeline: changed ones
// flush via the placement endpoint (idempotent PATCHes) before the classify.
async function flushAnchorStep<
  TData extends object,
  TRow extends DraftRowShape<TData>,
  TRequest,
  TResponse
>(config: {
  spec: AnchorStepSpec<TData, TRow, TRequest, TResponse>;
  drafts: WizardDrafts;
  baselines: WizardFlushBaselines;
  seeds: WizardSeeds;
  progress: WizardProgress;
  validation: WizardValidationContext;
  setRows: React.Dispatch<React.SetStateAction<TRow[]>>;
  mutate: (request: TRequest) => Promise<TResponse>;
  /** Commits changed protected rows via their placement endpoint (users step only). */
  commitProtectedRows?: (rows: Array<TRow & { entityId: string }>) => Promise<unknown>;
  commitBaseline: (rows: TRow[]) => void;
}): Promise<void> {
  const { spec, validation, progress } = config;
  if (!spec.canSubmit(validation)) return;
  const draftRows = spec.selectDrafts(config.drafts);
  if (draftRows.length === 0) return;
  const seedRows = spec.selectSeeds(config.seeds);
  const baseline = effectiveAnchorBaseline(
    spec.selectBaseline(config.baselines),
    spec.isDone(progress),
    seedRows,
    draftRows
  );

  const { allValid } = validateDraftRows(
    draftRows,
    spec.buildValidateRow(validation),
    spec.duplicateChecks,
    spec.buildCrossRowChecks(validation, progress)
  );
  if (!allValid) return;

  const protectedIds = spec.selectProtectedIds?.(validation, progress) ?? NO_PROTECTED_IDS;
  const { editableRows, editableBaseline, changedProtectedRows } = partitionProtectedRows(
    draftRows,
    baseline,
    seedRows,
    protectedIds
  );
  const protectedFlushed = changedProtectedRows.length > 0;
  if (protectedFlushed) {
    const commitProtectedRows = requireProtectedCommit(
      config.commitProtectedRows,
      changedProtectedRows.length
    );
    await commitProtectedRows(changedProtectedRows);
  }

  const classification = classifyAnchorDrafts(editableBaseline, editableRows);
  const removedIds = new Set(classification.kind === 'delta' ? classification.removedIds : []);
  const removedStillPending = (baseline ?? []).filter(
    (row) => row.entityId !== undefined && removedIds.has(row.entityId)
  );
  // A flushed placement must move the baseline even when the add-users lane
  // has nothing to send, or the committed protected edit keeps reading as
  // unsaved dirt; pending removals stay in the baseline as before.
  const commitProtectedOnlyBaseline = () => {
    if (protectedFlushed) config.commitBaseline([...draftRows, ...removedStillPending]);
  };
  if (classification.kind === 'unchanged') {
    commitProtectedOnlyBaseline();
    return;
  }

  const payload = buildAnchorImportPayload(classification);
  // Removals never autosave — deleting a row commits only via Weiter. A
  // deletion-only delta therefore has nothing to send (the contract rejects
  // an all-empty payload); the baseline keeps the rows as pending removals.
  if (payload.rows.length === 0 && !payload.updates) {
    commitProtectedOnlyBaseline();
    return;
  }
  const { idByRow, stampedRows } = await commitAnchorDelta({
    spec,
    validation,
    payload: { rows: payload.rows, ...(payload.updates && { updates: payload.updates }) },
    createRows: classification.newRows,
    allRows: draftRows,
    mutate: config.mutate
  });

  // Merge the returned ids into the LIVE rows by identity — a cell edited
  // while the request was in flight replaced its row object and keeps its
  // edit (the collision path re-converges it on the next flush).
  if (idByRow.size > 0) {
    config.setRows((current) =>
      current.map((row) => {
        const id = idByRow.get(row);
        return id && !row.entityId ? { ...row, entityId: id } : row;
      })
    );
  }

  config.commitBaseline([...stampedRows, ...removedStillPending]);
}

/**
 * Debounced silent autosave of the setup wizard (Semantics: whole-draft
 * validity gate; creates+updates only — removals commit exclusively via
 * Weiter; quiet failures). Owns its mutation instances so the footer's
 * Weiter spinner stays dark while autosave runs.
 */
export function useWizardAutosave(args: UseWizardAutosaveArgs): WizardAutosave {
  const createDepartmentsBulk = useCreateDepartmentsBulk();
  const initializeUsers = useInitializeUsers();
  const assignUserPlacement = useInitializeAssignUserPlacement();
  const initializeOrgFunctions = useInitializeOrgFunctions();
  const initializeSystems = useInitializeSystems();
  const initializeProcesses = useInitializeProcesses();

  const snapshot = {
    args,
    mutateDepartments: createDepartmentsBulk.mutateAsync,
    mutateUsers: initializeUsers.mutateAsync,
    mutateUserPlacement: assignUserPlacement.mutateAsync,
    mutateOrgFunctions: initializeOrgFunctions.mutateAsync,
    mutateSystems: initializeSystems.mutateAsync,
    mutateProcesses: initializeProcesses.mutateAsync
  };

  // The debounced flush must read fresh drafts/baselines/validators without
  // re-arming on every keystroke render.
  const latestRef = useLatestRef(snapshot);

  // Silent surface flush: resend-all — rows with `_id` update in place,
  // the rest create (a name collision comes back as an id and converts to
  // an update inside commitOrgFunctionSurface). Drafts stay put (Weiter's
  // clear is Weiter-only); the returned ids are stamped onto them.
  const flushSurfaceStep = useCallback(
    async (kind: 'role' | 'team'): Promise<void> => {
      const latest = latestRef.current;
      const { validation } = latest.args;
      const surfaceDrafts =
        kind === 'role' ? latest.args.drafts.functionDrafts : latest.args.drafts.teamDrafts;
      const setSurfaceDrafts =
        kind === 'role' ? latest.args.setFunctionDrafts : latest.args.setTeamDrafts;
      if (surfaceDrafts.rows.length + surfaceDrafts.memberships.length === 0) return;

      const rowsResult = validateDraftRows(
        surfaceDrafts.rows,
        validation.validateOrgFunctionRow,
        orgFunctionDuplicateKeys
      );
      const membershipsResult = validateDraftRows(
        surfaceDrafts.memberships,
        validation.validateMembershipRow,
        membershipDuplicateKeys
      );
      if (!rowsResult.allValid || !membershipsResult.allValid) return;

      const snapshotValue = serializeSurface(surfaceDrafts);
      const baselineKey = kind === 'role' ? 'functions' : 'teams';
      if (snapshotValue === latest.args.baselines[baselineKey]) return;

      const { idByRow } = await commitOrgFunctionSurface({
        kind,
        rows: surfaceDrafts.rows,
        memberships: surfaceDrafts.memberships,
        departments: validation.departmentsList,
        mutate: latest.mutateOrgFunctions
      });
      // Merge the returned ids into the LIVE rows by identity — a row edited
      // while the request was in flight keeps its edit and re-converges on the
      // next flush. The baseline snapshot carries the stamped SENT rows, or
      // gaining the ids would immediately read as new dirt.
      if (idByRow.size > 0) {
        setSurfaceDrafts((current) => ({
          ...current,
          rows: stampSurfaceRows(current.rows, idByRow)
        }));
      }
      const stampedSnapshot = serializeSurface({
        rows: stampSurfaceRows(surfaceDrafts.rows, idByRow),
        memberships: surfaceDrafts.memberships
      });
      latest.args.setBaselines((prev) => ({ ...prev, [baselineKey]: stampedSnapshot }));
      // `latestRef` is stable; it is listed because the lint rule cannot see the
      // `useRef` behind the custom hook.
    },
    [latestRef]
  );

  // Sequential over the covered steps; each flusher skips itself when clean/
  // invalid. A throw ends the round quietly — the remaining dirty steps retry
  // on the next change (errors are quiet: no toast, no error marks).
  const runFlushRound = useCallback(async (): Promise<boolean> => {
    const latest = latestRef.current;
    try {
      await flushAnchorStep({
        spec: departmentsStepSpec,
        drafts: latest.args.drafts,
        baselines: latest.args.baselines,
        seeds: latest.args.seeds,
        progress: latest.args.progress,
        validation: latest.args.validation,
        setRows: latest.args.setDepartments,
        mutate: latest.mutateDepartments,
        commitBaseline: (rows) =>
          latest.args.setBaselines((prev) => ({ ...prev, departments: rows }))
      });
      await flushAnchorStep({
        spec: usersStepSpec,
        drafts: latestRef.current.args.drafts,
        baselines: latestRef.current.args.baselines,
        seeds: latestRef.current.args.seeds,
        progress: latestRef.current.args.progress,
        validation: latestRef.current.args.validation,
        setRows: latestRef.current.args.setUsers,
        mutate: latestRef.current.mutateUsers,
        commitProtectedRows: (rows) =>
          commitPlacementRows(rows, latestRef.current.mutateUserPlacement),
        commitBaseline: (rows) =>
          latestRef.current.args.setBaselines((prev) => ({ ...prev, users: rows }))
      });
      await flushSurfaceStep('role');
      await flushSurfaceStep('team');
      await flushAnchorStep({
        spec: systemsStepSpec,
        drafts: latestRef.current.args.drafts,
        baselines: latestRef.current.args.baselines,
        seeds: latestRef.current.args.seeds,
        progress: latestRef.current.args.progress,
        validation: latestRef.current.args.validation,
        setRows: latestRef.current.args.setSystems,
        mutate: latestRef.current.mutateSystems,
        commitBaseline: (rows) =>
          latestRef.current.args.setBaselines((prev) => ({ ...prev, systems: rows }))
      });
      await flushAnchorStep({
        spec: processesStepSpec,
        drafts: latestRef.current.args.drafts,
        baselines: latestRef.current.args.baselines,
        seeds: latestRef.current.args.seeds,
        progress: latestRef.current.args.progress,
        validation: latestRef.current.args.validation,
        setRows: latestRef.current.args.setProcesses,
        mutate: latestRef.current.mutateProcesses,
        commitBaseline: (rows) =>
          latestRef.current.args.setBaselines((prev) => ({ ...prev, processes: rows }))
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
    // `latestRef` is stable; see the note on the previous flusher.
  }, [flushSurfaceStep, latestRef]);

  const [engineState, setEngineState] = useState<EngineSnapshot>({
    phase: 'idle',
    lastRound: 'none'
  });
  const runFlushRoundRef = useLatestRef(runFlushRound);

  const engineRef = useRef<ReturnType<typeof createAutosaveEngine> | null>(null);
  if (engineRef.current === null) {
    // Lazy-init ref: the engine is a plain state machine created exactly once.
    engineRef.current = createAutosaveEngine({
      runFlushRound: () => runFlushRoundRef.current(),
      onChange: setEngineState,
      debounceMs: AUTOSAVE_DEBOUNCE_MS
    });
  }
  const engine = engineRef.current;

  useEffect(
    function disposeEngineOnUnmount() {
      return () => engine.dispose();
    },
    [engine]
  );

  // Memoized: the classify/serialize pass over every draft is real work on big
  // imports, and the engine's lifecycle transitions re-render the page several
  // times per round without the answer changing.
  const draftsDirty = useMemo(
    () => computeWizardDirty(args.drafts, args.baselines, args.seeds, args.progress),
    [args.drafts, args.baselines, args.seeds, args.progress]
  );
  const isDirty =
    draftsDirty || engineState.phase === 'debouncing' || engineState.phase === 'flushing';

  return {
    status: deriveStatus(engineState, draftsDirty),
    isDirty,
    notifyChange: engine.notifyChange,
    settle: engine.settle,
    setSuspended: engine.setSuspended
  };
}
